/**
 * Calendar Types
 *
 * Normalized data structures for the Care calendar.
 * These types provide a unified model for rendering calendar items
 * regardless of their source (medications, appointments, period, fertile).
 */

import { toLocalDateString, parseLocalDate } from "@src/packages/utils/date";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import { isMedicationStopped } from "./medications/isMedicationStopped";
import { expandMedicationOccurrenceDates } from "./medication-occurrences";
import type {
  PredictionConfidence,
  PredictionStatus,
  PredictionWarning,
} from "./prediction/fertileWindow.types";
import { computeAppointmentStatus } from "./appointment-status";

// ============================================================================
// CalendarItem - Normalized structure for all calendar events
// ============================================================================

export type CalendarItemType =
  "medication" | "appointment" | "period" | "fertile" | "symptom";

export type CalendarItemStatus =
  | "due"
  | "taken"
  | "late"
  | "missed"
  | "upcoming"
  | "current"
  | "past"
  | "done"
  | "skipped"
  | "stopped";

/**
 * Color tokens for calendar items
 */
export const CALENDAR_COLORS = {
  medication: "#A78BFA", // Purple
  appointment: "#2DD4BF", // Teal
  period: "#F472B6", // Pink
  fertile: "#4ADE80", // Green
  symptom: "#FBBF24", // Amber
} as const;

/**
 * Normalized calendar item that the UI can render consistently.
 * All event types (medications, appointments, period, fertile) are
 * converted to this structure.
 */
export interface CalendarItem {
  /** Unique identifier */
  id: string;
  /** Type of calendar item */
  type: CalendarItemType;
  /**
   * Start date/time in ISO format.
   * For all-day items (period, fertile), this is the date at start of day.
   * For timed items (medication doses, appointments), includes the time.
   */
  startAt: string;
  /** End date/time in ISO format (optional, for multi-day items) */
  endAt?: string;
  /** Primary display title */
  title: string;
  /** Secondary display text (e.g., dosage, location) */
  subtitle?: string;
  /** Color for UI rendering */
  color: string;
  /** Status for timed items */
  status?: CalendarItemStatus;
  /** Additional metadata specific to the item type */
  meta?: CalendarItemMeta;
}

/**
 * Type-specific metadata for calendar items
 */
export interface CalendarItemMeta {
  // Medication-specific
  dose?: number | null;
  doseUnit?: string | null;
  medicationId?: string;
  medicationName?: string;
  /** Patient-medication administration route (e.g., "subcutaneous", "oral") */
  intakeType?: string | null;
  /** End date of the medication (YYYY-MM-DD), null if ongoing */
  endDate?: string | null;
  /** Scheduled times for the medication (e.g., ["09:00", "21:00"]) */
  timeTaken?: string[] | null;

  // Appointment-specific
  location?: string | null;
  consultationType?: string[] | null;
  isOnline?: boolean | null;
  meetingLink?: string | null;
  appointmentId?: string;
  /**
   * Ownership + temporal state (#2238) — preserved so any consumer can run
   * the canonical fail-closed `canManageAppointment` predicate and compute
   * real temporal status without rehydrating the full appointment.
   */
  ownership?: ApiAppointment["ownership"] | null;
  ownerId?: ApiAppointment["ownerId"] | null;
  timeZone?: string | null;
  startDate?: string;
  startTime?: string | null;
  appointmentEndDate?: string | null;
  endTime?: string | null;

  // Period-specific
  flowLevel?: "spotting" | "light" | "medium" | "heavy";

  // Fertile-specific
  isOvulationDay?: boolean;

  // Symptom-specific
  symptom?: string;
  symptomId?: string;

  // Common
  notes?: string | null;
  source?:
    | "doctor"
    | "patient"
    | "partner"
    | "computed"
    | "provider"
    | "system"
    | null;
  /** Mark as predicted (future projection, not logged data) */
  predicted?: boolean;
  /** Prediction context retained for confidence and warning presentation. */
  predictionConfidence?: PredictionConfidence;
  predictionStatus?: PredictionStatus;
  predictionWarnings?: PredictionWarning[];
}

// ============================================================================
// Day Summary - Aggregated data for calendar grid
// ============================================================================

/**
 * Summary of items for a single calendar day.
 * Used for rendering day markers in the monthly grid.
 */
export interface DaySummary {
  date: string; // YYYY-MM-DD
  hasMedication: boolean;
  hasAppointment: boolean;
  isPeriodDay: boolean;
  isFertileDay: boolean;
  isOvulationDay: boolean;
  isPredictedPeriodDay?: boolean;
  isPredictedFertileDay?: boolean;
  isPredictedOvulationDay?: boolean;
  itemCount: number;
}

// ============================================================================
// API Response Types (from backend)
// ============================================================================

/**
 * Medication from GET /{tenantId}/patients/{patientId}/medications
 */
export type ApiMedication = ResponseBody<
  paths["/{tenantId}/patients/{patientId}/medications"]["get"]
>["data"][number];

/**
 * Appointment from GET /{tenantId}/appointments
 */
export type ApiAppointment = ResponseBody<
  paths["/{tenantId}/appointments"]["get"]
>["data"][number];

// ============================================================================
// Normalization utilities
// ============================================================================

/**
 * Map of administration-route codes to display labels.
 */
export const INTAKE_TYPE_LABELS: Record<string, string> = {
  // Injection types
  subcutaneous: "Subcutaneous",
  intramuscular: "Intramuscular",
  intravenous: "Intravenous",
  injection: "Injection",
  // Oral types
  oral: "Oral",
  tablet: "Oral",
  capsule: "Oral",
  liquid: "Oral liquid",
  // Other routes
  topical: "Topical",
  vaginal: "Vaginal",
  intravaginal: "Vaginal",
  rectal: "Rectal",
  nasal: "Nasal",
  intranasal: "Nasal",
  sublingual: "Sublingual",
  inhaled: "Inhaled",
  transdermal: "Transdermal",
  patch: "Patch",
  drops: "Drops",
  suppository: "Suppository",
};

/**
 * Map of intake type codes to icon types.
 * Used to determine which icon to display for a medication.
 */
export type MedicationIconType =
  "pill" | "injection" | "drops" | "patch" | "inhaler" | "cream" | "default";

export const INTAKE_TYPE_ICONS: Record<string, MedicationIconType> = {
  // Injection types
  subcutaneous: "injection",
  intramuscular: "injection",
  intravenous: "injection",
  injection: "injection",
  // Oral types
  oral: "pill",
  tablet: "pill",
  capsule: "pill",
  liquid: "drops",
  // Other routes
  topical: "cream",
  vaginal: "cream",
  intravaginal: "cream",
  rectal: "cream",
  nasal: "drops",
  intranasal: "drops",
  sublingual: "pill",
  inhaled: "inhaler",
  transdermal: "patch",
  patch: "patch",
  drops: "drops",
  suppository: "cream",
};

/**
 * Format intake type for display.
 * Converts API codes like "subcutaneous" to "Subcutaneous".
 */
export function formatIntakeType(intakeType: string): string {
  const normalized = intakeType.toLowerCase().trim();
  return INTAKE_TYPE_LABELS[normalized] ?? intakeType;
}

/**
 * Get the icon type for an intake type.
 */
export function getIntakeTypeIcon(
  intakeType: string | null | undefined,
): MedicationIconType {
  if (!intakeType) return "default";
  const normalized = intakeType.toLowerCase().trim();
  return INTAKE_TYPE_ICONS[normalized] ?? "default";
}

/**
 * Convert API medication to CalendarItem(s).
 * A single medication may produce multiple calendar items if it has multiple doses per day.
 */
export function normalizeMedication(
  med: ApiMedication,
  range: { startDate?: string | null; endDate?: string | null } = {},
): CalendarItem[] {
  if (isMedicationStopped(med.notes)) {
    return [];
  }

  const items: CalendarItem[] = [];

  // Parse time taken if available
  const times =
    med.timeTaken && med.timeTaken.length > 0 ? med.timeTaken : ["09:00"]; // Default time if not specified

  // A course runs every day from start through end (inclusive) — expand via
  // the canonical adapter so this path agrees with the concept calendar
  // (#2223). Without a range, an open-ended medication emits its start day.
  const dates = expandMedicationOccurrenceDates(med, range);

  // Create an item for each occurrence date and dose time
  for (const date of dates) {
    for (const time of times) {
      const startAt = `${date}T${time}:00`;
      // displayName is the patient's persisted label override (#319).
      const medicationName =
        med.displayName ||
        med.medication?.name ||
        med.rawMedicationName ||
        med.medication?.molecule ||
        "Medication";
      const intakeType = med.route ?? null;

      // Build subtitle: just show dose (e.g., "200mg")
      const subtitle =
        med.dose && med.doseUnit ? `${med.dose} ${med.doseUnit}` : undefined;

      items.push({
        id: `med-${med.id}-${date}-${time}`,
        type: "medication",
        startAt,
        title: medicationName,
        subtitle,
        color: CALENDAR_COLORS.medication,
        status: "due", // TODO: Compute actual status based on current time
        meta: {
          dose: med.dose,
          doseUnit: med.doseUnit,
          medicationId: med.id,
          medicationName,
          intakeType,
          endDate: med.endDate,
          notes: med.notes,
          source: med.source,
        },
      });
    }
  }

  return items;
}

/**
 * Convert API appointment to CalendarItem.
 *
 * Status is computed from the supplied clock against the appointment's own
 * timezone, and ownership/temporal metadata is preserved so consumers can
 * gate mutations via `canManageAppointment` and re-derive status without
 * rehydrating the full record (#2238).
 */
export function normalizeAppointment(
  appt: ApiAppointment,
  now: Date = new Date(),
): CalendarItem {
  // Combine date and time
  const startAt = `${appt.startDate}T${appt.startTime || "00:00"}:00`;

  return {
    id: `appt-${appt.id}`,
    type: "appointment",
    startAt,
    endAt: appt.endDate
      ? `${appt.endDate}T${appt.endTime || "23:59"}:00`
      : undefined,
    title: appt.title,
    subtitle: appt.meetingRoom || (appt.isOnline ? "Online" : undefined),
    color: CALENDAR_COLORS.appointment,
    status: computeAppointmentStatus(
      {
        startDate: appt.startDate,
        startTime: appt.startTime,
        endDate: appt.endDate,
        endTime: appt.endTime,
        timeZone: appt.timeZone,
      },
      now,
    ),
    meta: {
      location: appt.meetingRoom,
      consultationType: appt.consultationType,
      isOnline: appt.isOnline,
      meetingLink: appt.meetingLink,
      appointmentId: appt.id,
      notes: appt.description,
      ownership: appt.ownership,
      ownerId: appt.ownerId,
      source: appt.source,
      timeZone: appt.timeZone ?? null,
      startDate: appt.startDate,
      startTime: appt.startTime ?? null,
      appointmentEndDate: appt.endDate ?? null,
      endTime: appt.endTime ?? null,
    },
  };
}

/**
 * Create period day CalendarItem.
 */
export function createPeriodItem(
  date: string,
  flowLevel?: "spotting" | "light" | "medium" | "heavy",
): CalendarItem {
  return {
    id: `period-${date}`,
    type: "period",
    startAt: `${date}T00:00:00`,
    title: "Period Day",
    subtitle: flowLevel
      ? `${flowLevel.charAt(0).toUpperCase()}${flowLevel.slice(1)} flow`
      : undefined,
    color: CALENDAR_COLORS.period,
    meta: {
      flowLevel,
      source: "patient",
    },
  };
}

/**
 * Create fertile day CalendarItem.
 */
export function createFertileItem(
  date: string,
  isOvulationDay: boolean = false,
): CalendarItem {
  return {
    id: `fertile-${date}`,
    type: "fertile",
    startAt: `${date}T00:00:00`,
    title: isOvulationDay ? "Ovulation Day" : "Fertile Window",
    color: CALENDAR_COLORS.fertile,
    meta: {
      isOvulationDay,
      source: "computed",
    },
  };
}

/**
 * Generate period days from start and end dates.
 */
export function generatePeriodDays(
  startDate: string,
  endDate: string | null,
): string[] {
  const days: string[] = [];
  const start = parseLocalDate(startDate);
  const end = endDate ? parseLocalDate(endDate) : new Date(start);

  // Default to 5 days if no end date
  if (!endDate) {
    end.setDate(start.getDate() + 4);
  }

  const current = new Date(start);
  while (current <= end) {
    days.push(toLocalDateString(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

/**
 * Build day summary map from calendar items.
 */
export function buildDaySummaryMap(
  items: CalendarItem[],
): Map<string, DaySummary> {
  const summaryMap = new Map<string, DaySummary>();

  for (const item of items) {
    const dateKey = item.startAt.split("T")[0]!;

    if (!summaryMap.has(dateKey)) {
      summaryMap.set(dateKey, {
        date: dateKey,
        hasMedication: false,
        hasAppointment: false,
        isPeriodDay: false,
        isFertileDay: false,
        isOvulationDay: false,
        isPredictedPeriodDay: false,
        isPredictedFertileDay: false,
        isPredictedOvulationDay: false,
        itemCount: 0,
      });
    }

    const summary = summaryMap.get(dateKey)!;
    summary.itemCount++;

    switch (item.type) {
      case "medication":
        summary.hasMedication = true;
        break;
      case "appointment":
        summary.hasAppointment = true;
        break;
      case "period":
        if (item.meta?.predicted) {
          summary.isPredictedPeriodDay = true;
        } else {
          summary.isPeriodDay = true;
        }
        break;
      case "fertile":
        if (item.meta?.predicted) {
          summary.isPredictedFertileDay = true;
          if (item.meta?.isOvulationDay) {
            summary.isPredictedOvulationDay = true;
          }
        } else {
          summary.isFertileDay = true;
          if (item.meta?.isOvulationDay) {
            summary.isOvulationDay = true;
          }
        }
        break;
    }
  }

  return summaryMap;
}

/**
 * Get items for a specific date, sorted by time.
 */
export function getItemsForDate(
  items: CalendarItem[],
  date: string,
): CalendarItem[] {
  return items
    .filter((item) => item.startAt.startsWith(date))
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
}

/**
 * Get upcoming items (sorted by startAt, soonest first).
 */
export function getUpcomingItems(
  items: CalendarItem[],
  now: Date,
  limit: number = 5,
): CalendarItem[] {
  // Create a local datetime string for comparison (YYYY-MM-DDTHH:MM:SS format)
  // This matches the format used in item.startAt
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const nowLocalStr = `${toLocalDateString(now)}T${hours}:${minutes}:${seconds}`;

  return items
    .filter(
      (item) =>
        item.startAt >= nowLocalStr &&
        (item.type === "medication" || item.type === "appointment"),
    )
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
    .slice(0, limit);
}

/**
 * Format a relative date label for "Up Next" display.
 */
export function formatRelativeDateLabel(itemDate: string, now: Date): string {
  const itemDateObj = new Date(itemDate);
  const todayStr = toLocalDateString(now);
  const itemDateStr = itemDate.split("T")[0];

  // Check if today
  if (itemDateStr === todayStr) {
    return "Due Today";
  }

  // Check if tomorrow
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = toLocalDateString(tomorrow);
  if (itemDateStr === tomorrowStr) {
    return "Tomorrow";
  }

  // Return day name for this week
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return dayNames[itemDateObj.getDay()]!;
}

// ============================================================================
// Period Block Detection (shared utility)
// ============================================================================

/**
 * Extracts period blocks from calendar items.
 * A period block is a group of consecutive period days.
 *
 * @param items - All calendar items (will filter for period type)
 * @returns Array of period blocks, each block is an array of date strings sorted ascending
 */
export function extractPeriodBlocks(items: CalendarItem[]): string[][] {
  // Get all period days, sorted by date ascending
  const periodDays = items
    .filter((item) => item.type === "period")
    .map((item) => item.startAt.split("T")[0]!)
    .sort((a, b) => a.localeCompare(b));

  if (periodDays.length === 0) {
    return [];
  }

  // Get unique dates (in case of duplicates)
  const uniquePeriodDays = [...new Set(periodDays)];

  // Find period blocks (consecutive days)
  const periodBlocks: string[][] = [];
  let currentBlock: string[] = [];

  for (let i = 0; i < uniquePeriodDays.length; i++) {
    const currentDate = uniquePeriodDays[i]!;

    if (currentBlock.length === 0) {
      currentBlock.push(currentDate);
    } else {
      const lastDate = currentBlock[currentBlock.length - 1]!;
      const lastDateObj = parseLocalDate(lastDate);
      const currentDateObj = parseLocalDate(currentDate);

      // Check if consecutive (1 day apart)
      const diffDays = Math.round(
        (currentDateObj.getTime() - lastDateObj.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      if (diffDays === 1) {
        // Consecutive, add to current block
        currentBlock.push(currentDate);
      } else {
        // Gap found, start new block
        periodBlocks.push(currentBlock);
        currentBlock = [currentDate];
      }
    }
  }

  // Don't forget the last block
  if (currentBlock.length > 0) {
    periodBlocks.push(currentBlock);
  }

  return periodBlocks;
}
